import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, SOURCES } from "@/lib/api";
import { Users, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Clients() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ source: "", q: "" });
  const [exporting, setExporting] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const reqId = useRef(0);

  function params() {
    const p = {};
    if (filter.source) p.source = filter.source;
    if (filter.q) p.q = filter.q;
    return p;
  }

  async function load() {
    const id = ++reqId.current;
    const { data } = await api.get("/clients", { params: params() });
    if (id === reqId.current) setItems(data);
  }

  async function exportExcel() {
    setExporting(true);
    try {
      const res = await api.get("/export/clients.xlsx", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clienti-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Export descărcat");
    } catch {
      toast.error("Exportul a eșuat");
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("data:refresh", h);
    return () => window.removeEventListener("data:refresh", h);
  }, [filter]);

  return (
    <div className="sm:pl-52 space-y-4" data-testid="clients-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Lead-uri</div>
          <h1 className="font-display text-3xl tracking-tight font-medium">Clienți</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500 tabular" data-testid="clients-count">{items.length} rezultate</span>
          {isAdmin && (
            <button
              onClick={exportExcel}
              disabled={exporting}
              data-testid="clients-export-excel-btn"
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-60"
            >
              <FileSpreadsheet className="w-4 h-4" /> {exporting ? "Se generează…" : "Export Excel"}
            </button>
          )}
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        <input
          data-testid="clients-search"
          placeholder="Caută nume, telefon, email..."
          value={filter.q}
          onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
          className="flex-1 min-w-[180px] bg-white border border-slate-200 rounded-full px-4 py-2 text-sm"
        />
        <select
          data-testid="clients-filter-source"
          value={filter.source}
          onChange={(e) => setFilter((f) => ({ ...f, source: e.target.value }))}
          className="bg-white border border-slate-200 rounded-full px-3 py-2 text-sm"
        >
          <option value="">Toate sursele</option>
          {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {items.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
          <Users className="w-10 h-10 mx-auto text-slate-300" />
          <div className="mt-3 font-display text-lg">Niciun client</div>
          <div className="text-sm text-slate-500 mt-1">Adaugă-ți primul lead pentru a-l vedea aici.</div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {items.map((c) => (
          <Link
            data-testid={`client-row-${c.id}`}
            key={c.id}
            to={`/clients/${c.id}`}
            className="flex items-center justify-between px-4 py-3 hover:bg-slate-50"
          >
            <div>
              <div className="font-medium">{c.name}</div>
              <div className="text-xs text-slate-500">{c.phone || "—"} {c.email ? "· " + c.email : ""}</div>
            </div>
            {c.source && (
              <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-700 px-2 py-1 rounded-full">{c.source}</span>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
